import { pascalCase } from "change-case";
import * as React from "react";

import { KtData } from "./scaffolding/kotlin/KtData";
import {
  scaffoldDefaultValue,
  scaffoldType,
} from "./scaffolding/scaffolders/scaffoldModels";
import { scaffoldSchema } from "./scaffolding/scaffolders/scaffoldSchemas";
import { createSchematic, Schematic } from "./Schematic";
import { ClassBuilder } from "./schematicBuilders/ClassBuilder";
import { EnumBuilder } from "./schematicBuilders/EnumBuilder";
import { ListableBuilder } from "./schematicBuilders/ListableBuilder";
import { code } from "./util/code";
import { joinPaths } from "./util/join";

export const NULLABLE_SCHEMA = "io.kform.schemas.NullableSchema";

/** Information about a kind of schematic. */
export interface SchematicKind {
  kind: string;
  internal?: boolean;
  schema: string;
  package?: string;
  name?: string;
  defaultNullable?: boolean;
  nullable?: boolean;
  defaultValue: string | ((schematic: Schematic, data: KtData) => string);
  builder?: React.FC<any>;
  initChildren?: (childName: string) => Schematic[];
  defaultPackageSuffix?: (childName: string) => string;
  defaultName?: (childName: string) => string;
  scaffoldModel?: (schematic: Schematic, data: KtData) => string;
  scaffoldType?: (schematic: Schematic, data: KtData) => string;
  scaffoldSchema?: (schematic: Schematic, data: KtData) => string;
}

export const anySchematicKind: SchematicKind = {
  kind: "Any",
  package: "kotlin",
  name: "Any",
  schema: "io.kform.schemas.AnySchema",
  nullable: false,
  defaultValue: "null",
  scaffoldType: () => "Any?",
};
export const bigDecimalSchematicKind: SchematicKind = {
  kind: "BigDecimal",
  package: "io.kform.datatypes",
  name: "BigDecimal",
  schema: "io.kform.schemas.BigDecimalSchema",
  defaultNullable: true,
  defaultValue: "BigDecimal.ZERO",
};
export const bigIntegerSchematicKind: SchematicKind = {
  kind: "BigInteger",
  package: "io.kform.datatypes",
  name: "BigInteger",
  schema: "io.kform.schemas.BigIntegerSchema",
  defaultNullable: true,
  defaultValue: "BigInteger.ZERO",
};
export const booleanSchematicKind: SchematicKind = {
  kind: "Boolean",
  package: "kotlin",
  name: "Boolean",
  schema: "io.kform.schemas.BooleanSchema",
  defaultValue: "false",
};
export const byteSchematicKind: SchematicKind = {
  kind: "Byte",
  package: "kotlin",
  name: "Byte",
  schema: "io.kform.schemas.ByteSchema",
  defaultNullable: true,
  defaultValue: "0.toByte()",
};
export const charSchematicKind: SchematicKind = {
  kind: "Char",
  package: "kotlin",
  name: "Char",
  schema: "io.kform.schemas.CharSchema",
  defaultNullable: true,
  defaultValue: "0.toChar()",
};
export const classSchematicKind: SchematicKind = {
  kind: "Class",
  schema: "io.kform.schemas.ClassSchema",
  defaultValue: (schematic) => `${schematic.name}()`,
  builder: ClassBuilder,
  initChildren: () => [],
  defaultPackageSuffix: (childName: string) => childName.toLowerCase(),
  defaultName: (childName: string) => pascalCase(childName),
  scaffoldModel: (schematic, data) =>
    code`
      data class ${schematic.name}(
          ${schematic.children
            ?.map(
              (childSchematic) =>
                `var ${childSchematic.childName}: ${scaffoldType(
                  childSchematic,
                  {
                    ...data,
                    currentPath: joinPaths(
                      data.currentPath,
                      childSchematic.childName,
                    ),
                  },
                )} = ${scaffoldDefaultValue(childSchematic, {
                  ...data,
                  currentPath: joinPaths(
                    data.currentPath,
                    childSchematic.childName,
                  ),
                })}`,
            )
            .join(",\n")}
      )
    `,
  scaffoldSchema: (schematic, data) =>
    code`
      ClassSchema {
          ${schematic.children
            ?.map(
              (childSchematic) =>
                `${schematic.name}::${childSchematic.childName} { ${scaffoldSchema(
                  childSchematic,
                  {
                    ...data,
                    currentPath: joinPaths(
                      data.currentPath,
                      childSchematic.childName,
                    ),
                  },
                )} }`,
            )
            .join("\n")}
      }
    `,
};
export const doubleSchematicKind: SchematicKind = {
  kind: "Double",
  package: "kotlin",
  name: "Double",
  schema: "io.kform.schemas.DoubleSchema",
  defaultNullable: true,
  defaultValue: "0.0",
};
export const enumSchematicKind: SchematicKind = {
  kind: "Enum",
  schema: "io.kform.schemas.EnumSchema",
  defaultNullable: true,
  defaultValue: (schematic) =>
    `${schematic.name}.${schematic.children?.[0].childName}`,
  builder: EnumBuilder,
  initChildren: () => [],
  defaultName: (childName: string) => pascalCase(childName),
  scaffoldModel: (schematic) =>
    code`
      enum class ${schematic.name} {
          ${schematic.children
            ?.map((childSchematic) => childSchematic.childName!)
            .join(",\n")}
      }
    `,
};
export const fileSchematicKind: SchematicKind = {
  kind: "File",
  package: "io.kform.datatypes",
  name: "File",
  schema: "io.kform.schemas.FileSchema",
  defaultNullable: true,
  defaultValue: (_schematic, data) => {
    data.currentFile.imports.add("io.kform.datatypes.emplyPlaceholderFile");
    return "emplyPlaceholderFile()";
  },
};
export const floatSchematicKind: SchematicKind = {
  kind: "Float",
  package: "kotlin",
  name: "Float",
  schema: "io.kform.schemas.FloatSchema",
  defaultNullable: true,
  defaultValue: "0f",
};
export const instantSchematicKind: SchematicKind = {
  kind: "Instant",
  package: "kotlinx.datetime",
  name: "Instant",
  schema: "io.kform.schemas.InstantSchema",
  defaultNullable: true,
  defaultValue: "Instant.fromEpochMilliseconds(0)",
};
export const intSchematicKind: SchematicKind = {
  kind: "Int",
  package: "kotlin",
  name: "Int",
  schema: "io.kform.schemas.IntSchema",
  defaultNullable: true,
  defaultValue: "0",
};
export const listSchematicKind: SchematicKind = {
  kind: "List",
  package: "kotlin.collections",
  name: "List",
  schema: "io.kform.schemas.ListSchema",
  defaultValue: "emptyList()",
  builder: ListableBuilder,
  initChildren: () => [createSchematic()],
  scaffoldType: (schematic, data) =>
    `List<${scaffoldType(schematic.children![0], {
      ...data,
      currentPath: joinPaths(data.currentPath, "*"),
    })}>`,
  scaffoldSchema: (schematic, data) =>
    `ListSchema { ${scaffoldSchema(schematic.children![0], {
      ...data,
      currentPath: joinPaths(data.currentPath, "*"),
    })} }`,
};
export const localDateSchematicKind: SchematicKind = {
  kind: "LocalDate",
  package: "kotlinx.datetime",
  name: "LocalDate",
  schema: "io.kform.schemas.LocalDateSchema",
  defaultNullable: true,
  defaultValue: (_schematic, data) => {
    data.currentFile.imports.add("kotlinx.datetime.Instant");
    data.currentFile.imports.add("kotlinx.datetime.TimeZone");
    return "Instant.fromEpochMilliseconds(0).toLocalDateTime(TimeZone.UTC).date";
  },
};
export const localDateTimeSchematicKind: SchematicKind = {
  kind: "LocalDateTime",
  package: "kotlinx.datetime",
  name: "LocalDateTime",
  schema: "io.kform.schemas.LocalDateTimeSchema",
  defaultNullable: true,
  defaultValue: (_schematic, data) => {
    data.currentFile.imports.add("kotlinx.datetime.Instant");
    data.currentFile.imports.add("kotlinx.datetime.TimeZone");
    return "Instant.fromEpochMilliseconds(0).toLocalDateTime(TimeZone.UTC)";
  },
};
export const longSchematicKind: SchematicKind = {
  kind: "Long",
  package: "kotlin",
  name: "Long",
  schema: "io.kform.schemas.LongSchema",
  defaultNullable: true,
  defaultValue: "0L",
};
export const shortSchematicKind: SchematicKind = {
  kind: "Short",
  package: "kotlin",
  name: "Short",
  schema: "io.kform.schemas.ShortSchema",
  defaultNullable: true,
  defaultValue: "0.toShort()",
};
export const stringSchematicKind: SchematicKind = {
  kind: "String",
  package: "kotlin",
  name: "String",
  schema: "io.kform.schemas.StringSchema",
  defaultValue: '""',
};
export const tableSchematicKind: SchematicKind = {
  kind: "Table",
  package: "io.kform.datatypes",
  name: "Table",
  schema: "io.kform.schemas.TableSchema",
  defaultValue: (_schematic, data) => {
    data.currentFile.imports.add("io.kform.datatypes.tableOf");
    return "tableOf()";
  },
  builder: ListableBuilder,
  initChildren: () => [createSchematic()],
  scaffoldType: (schematic, data) =>
    `Table<${scaffoldType(schematic.children![0], {
      ...data,
      currentPath: joinPaths(data.currentPath, "*"),
    })}>`,
  scaffoldSchema: (schematic, data) =>
    `TableSchema { ${scaffoldSchema(schematic.children![0], {
      ...data,
      currentPath: joinPaths(data.currentPath, "*"),
    })} }`,
};

export const defaultSchematicKinds = [
  anySchematicKind,
  bigDecimalSchematicKind,
  bigIntegerSchematicKind,
  booleanSchematicKind,
  byteSchematicKind,
  charSchematicKind,
  classSchematicKind,
  doubleSchematicKind,
  enumSchematicKind,
  fileSchematicKind,
  floatSchematicKind,
  instantSchematicKind,
  intSchematicKind,
  listSchematicKind,
  localDateSchematicKind,
  localDateTimeSchematicKind,
  longSchematicKind,
  shortSchematicKind,
  stringSchematicKind,
  tableSchematicKind,
];
